import { HttpErrorResponse, HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { NEVER, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import moment from 'moment';
import { StorageService } from 'ngx-webstorage-service';

import { environment } from '@environment';

import { LoadingSpinnerService } from '../loading-spinner/service/loading-spinner.service';
import { PhxConstants } from '../model';
import { AppStorageService } from '../services/app-storage.service';
import { ConnectivityService } from '../services/connectivity.service';
import { TokenRefreshService } from '../services/token-refresh.service';

import HTTPResponseStatus = PhxConstants.HTTPResponseStatus;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly endpointsToIgnoreAuthError: string[] = ['Account/Logout'];
  private readonly refreshTokenErrorKeys: string[] = ['invalid_grant', 'invalid_clientId'];

  constructor(
    private tokenRefreshService: TokenRefreshService,
    private connectivityService: ConnectivityService,
    private loadingSpinnerService: LoadingSpinnerService,
    @Inject(AppStorageService) private storageService: StorageService
  ) {}

  private static addDefaultHeaders(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return request;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const bearerToken = this.storageService.get('BearerToken');
    const bearerTokenExpiresOn = this.storageService.get('BearerTokenExpiresOn');

    const twoMinutesFromNow = moment().add(2, 'm');
    const getTokenCall = req.url.indexOf('token') !== -1;
    const isApiRequest = req.url.startsWith(environment.apiUrl);
    // A login attempt (grant_type=password) against an empty session has nothing for
    // logoutAndRedirect() to tear down — skipping it here just avoids the hard reload that
    // wipes the caller's own rejected-credentials error before it can render. The `!bearerToken`
    // check keeps this scoped to that case: LoginComponent clears storage before calling
    // AuthService.login(), but the SSO and registration callers of the same method do not, so a
    // rejected attempt there can still have a real prior session in storage — that case must
    // keep falling through to logoutAndRedirect() exactly as before, so it still gets cleared.
    const isLoginAttempt = getTokenCall && !bearerToken && typeof req.body === 'string' && req.body.includes('grant_type=password');

    if (!getTokenCall && !this.tokenRefreshService.preemptiveSuppressed && bearerTokenExpiresOn && moment(bearerTokenExpiresOn) < twoMinutesFromNow) {
      const response$ = this.refreshAndRetry(req, next);
      return isApiRequest ? this.reportOutcome(response$) : response$;
    }

    const response$ = next.handle(TokenInterceptor.addDefaultHeaders(req, bearerToken)).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === HTTPResponseStatus.Unauthorized && !isLoginAttempt && !this.endpointsToIgnoreAuthError.some(endpoint => req.url.includes(endpoint))) {
            const refreshToken = this.storageService.get('RefreshToken');

            if (refreshToken && bearerToken) {
              this.tokenRefreshService.preemptiveSuppressed = false;
              return this.refreshAndRetry(req, next);
            }

            // No refresh token to fall back on — the session can't be salvaged.
            // logoutAndRedirect() already covers the user-facing side (toast + redirect to
            // login) via a hard document.location.replace(), so no subscriber — Observable or
            // promise-based (lastValueFrom/firstValueFrom) — ever needs to see this complete or
            // error. NEVER means it just never does, rather than rethrowing this in-flight
            // request's expired-session error to Rollbar as an application error, or completing
            // empty and having *ValueFrom manufacture a less useful EmptyError in its place.
            // NEVER also means this request's own finalize()/catchError never fires to hide its
            // spinner, so hide it here instead of in logoutAndRedirect() — that would re-hide it
            // for the ensureFreshToken()/performRefresh() callers that still complete via EMPTY
            // and already reach finalize() themselves, sending showCount negative for no reason.
            this.tokenRefreshService.logoutAndRedirect();
            this.loadingSpinnerService.hideAll();
            return NEVER;
          }

          if (err.status === HTTPResponseStatus.BadRequest && !isLoginAttempt && err.error && this.refreshTokenErrorKeys.includes(err.error.error)) {
            this.tokenRefreshService.logoutAndRedirect();
          }
        }

        return throwError(() => err);
      })
    );

    // Only requests to our own API say anything about FBO's connectivity. A successful
    // third-party/asset request shouldn't clear an offline state, and an unrelated CORS/network
    // failure to some other origin shouldn't raise one.
    return isApiRequest ? this.reportOutcome(response$) : response$;
  }

  private refreshAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.tokenRefreshService.ensureFreshToken().pipe(
      switchMap(token => next.handle(TokenInterceptor.addDefaultHeaders(request, token)))
    );
  }

  // Reports every real request's outcome to ConnectivityService so active usage keeps
  // connectivity status current without a dedicated probe. status === 0 means the request
  // never reached the server (offline/DNS/CORS-preflight failure); any other status — including
  // 4xx/5xx — means the server was reachable, whatever else went wrong with the request itself.
  private reportOutcome(source: Observable<HttpEvent<any>>): Observable<HttpEvent<any>> {
    return source.pipe(
      tap(event => {
        // next.handle() emits a Sent event before the eventual Response; only the latter
        // confirms the server actually answered.
        if (event.type === HttpEventType.Response) {
          this.connectivityService.reportReachable(true);
        }
      }),
      catchError(err => {
        this.connectivityService.reportReachable(!(err instanceof HttpErrorResponse) || err.status !== 0);
        return throwError(() => err);
      })
    );
  }
}
